
import {
  Component, Watch, Prop, Vue, PropSync,
} from 'vue-property-decorator';
import { EstimateDataType } from '@/features/estimate/types';
import {
  Appointment,
  AppointmentType,
  InProgress, ShipFrom, ShipTo,
} from '../../types';
import { getAppointmentDefaultDate, getAppointmentTime } from '../../helpers/createShipment';

export type ShipFromType = Pick<ShipFrom, 'isInside' | 'isTailGate' | 'isTradeShow' | 'isLimitedAccess'>;

export type ShipToType = Pick<ShipTo, 'isInside' | 'isTailGate' | 'isTradeShow' | 'isLimitedAccess' | 'appointment'>

@Component
export default class FreightOptions extends Vue {
  @PropSync('isFormChanged', { required: false, default: false }) syncedIsFormChanged!: boolean;

  // this flag will determine whether to create shipment or just estimate
  @Prop({ required: false, default: false }) isJustEstimate!: boolean;

  @Prop({ required: false, default: true }) autoSync!: boolean;

  private shipFrom: ShipFromType = {
    isInside: false,
    isTailGate: false,
    isTradeShow: false,
    isLimitedAccess: false,
  }

  private shipTo: ShipToType = {
    isInside: false,
    isTailGate: false,
    isTradeShow: false,
    isLimitedAccess: false,
    appointment: {
      appointmentType: 'None',
      date: getAppointmentDefaultDate(),
      time: getAppointmentTime(),
    },
  };

  private isAppointmentOptions = false;

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get estimateDataFromStore(): EstimateDataType {
    return this.$store.state.estimate.estimateData;
  }

  get appointmentStatus(): keyof typeof AppointmentType {
    if (!this.isAppointmentOptions || !this.shipTo.appointment) {
      return 'None';
    }

    const { date } = this.shipTo.appointment;

    return date ? 'Scheduled' : 'Required';
  }

  @Watch('isInProgressData', { immediate: true })
  private async replaceShipmentData(newValue: number) {
    await this.$nextTick();

    if (!newValue) return;
    if (this.isJustEstimate) return;

    const {
      fromAddress,
      toAddress,
    } = this.inProgressData;

    this.setFormData(fromAddress, toAddress);
    this.$emit('update', this.shipFrom, this.shipTo);
  }

  @Watch('isJustEstimate', { immediate: true })
  private replaceEstimateData(newValue: boolean): void {
    if (!newValue) return;

    const { fromAddress, toAddress } = this.estimateDataFromStore;

    this.setFormData(fromAddress, toAddress);
  }

  private setFormData(fromAddress: ShipFromType, toAddress: ShipToType) {
    this.shipFrom = {
      isInside: fromAddress.isInside,
      isTailGate: fromAddress.isTailGate,
      isTradeShow: fromAddress.isTradeShow,
      isLimitedAccess: fromAddress.isLimitedAccess,
    };

    const {
      appointmentType, date, time,
    } = toAddress.appointment || {};

    this.shipTo = {
      isInside: toAddress.isInside,
      isTailGate: toAddress.isTailGate,
      isTradeShow: toAddress.isTradeShow,
      isLimitedAccess: toAddress.isLimitedAccess,
      appointment: {
        appointmentType: appointmentType || 'None',
        date: date || getAppointmentDefaultDate(),
        time: getAppointmentTime(time),
      },
    };

    this.isAppointmentOptions = this.shipTo.appointment?.appointmentType !== 'None';
  }

  private updateInProgressData(autoSync = true) {
    if (!autoSync) {
      this.$emit('update', this.shipFrom, {
        ...this.shipTo,
        appointment: this.getAppointmentPayload(),
      });
      return;
    }

    if (this.isJustEstimate) {
      this.syncEstimateData();
      return;
    }

    this.syncInProgressData();
  }

  private syncInProgressData() {
    const payload: InProgress = {
      ...this.inProgressData,
      fromAddress: {
        ...this.inProgressData.fromAddress,
        ...this.shipFrom,
      },
      toAddress: {
        ...this.inProgressData.toAddress,
        ...this.shipTo,
        appointment: this.getAppointmentPayload(),
      },
    };

    this.$store.dispatch('setAndSyncInProgressData', payload);
  }

  private syncEstimateData() {
    const estimateDataStore = this.estimateDataFromStore;

    const payload: EstimateDataType = {
      ...estimateDataStore,
      fromAddress: {
        ...estimateDataStore.fromAddress,
        ...this.shipFrom,
      },
      toAddress: {
        ...estimateDataStore.toAddress,
        ...this.shipTo,
        appointment: this.getAppointmentPayload(),
      },
    };

    this.$store.commit('setEstimateData', payload);
  }

  private getAppointmentPayload(): Appointment | undefined {
    if (!this.isAppointmentOptions) return undefined;

    const { date, time } = this.shipTo.appointment || {};

    return {
      appointmentType: this.appointmentStatus,
      date: date || getAppointmentDefaultDate(),
      time,
    };
  }
}
